import { useEffect, useRef, useState } from 'react'
import { Info, Sparkles, User } from 'lucide-react'
import type { FieldSource } from '@ispot/shared'

interface Props {
  source: FieldSource
  rationale?: string
  modelUsed?: string
  generatedAt?: string
  editedAt?: string
  /** Human-readable hint shown for fields without rationale (e.g. the grounding hint). */
  emptyHint?: string
}

/**
 * Inline "i" button that reveals where a number came from. Shows the AI's
 * rationale text plus provenance metadata (model, when it ran). For non-AI
 * values, surfaces the editedAt timestamp if present.
 */
export function RationaleHint({
  source,
  rationale,
  modelUsed,
  generatedAt,
  editedAt,
  emptyHint,
}: Props) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const hasContent = !!rationale || source === 'ai' || source === 'edited' || !!emptyHint
  if (!hasContent) return null

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-5 w-5 items-center justify-center rounded-full transition-colors hover:bg-[var(--color-warm-200)]"
        title="Why this number?"
        aria-label="Show reasoning"
      >
        <Info className="h-3 w-3" style={{ color: 'var(--color-text-muted)' }} />
      </button>
      {open ? (
        <div
          className="absolute right-0 top-full z-30 mt-1 w-[360px] rounded-md border p-3 text-left shadow-lg"
          style={{
            backgroundColor: 'var(--color-bg, white)',
            borderColor: 'var(--color-border-subtle)',
          }}
        >
          <Header source={source} />
          <div className="mt-2 text-xs leading-relaxed" style={{ color: 'var(--color-text)' }}>
            {rationale ? (
              <p className="whitespace-pre-wrap">{rationale}</p>
            ) : (
              <p className="italic" style={{ color: 'var(--color-text-muted)' }}>
                {source === 'manual'
                  ? 'Entered manually — no reasoning recorded.'
                  : emptyHint || 'No reasoning recorded for this field.'}
              </p>
            )}
          </div>
          <Metadata
            modelUsed={modelUsed}
            generatedAt={generatedAt}
            editedAt={editedAt}
            source={source}
          />
        </div>
      ) : null}
    </div>
  )
}

function Header({ source }: { source: FieldSource }) {
  if (source === 'ai') {
    return (
      <div className="flex items-center gap-1.5">
        <Sparkles className="h-3 w-3" style={{ color: 'var(--color-market)' }} />
        <span
          className="text-[10px] font-semibold uppercase tracking-wider"
          style={{ color: 'var(--color-market)' }}
        >
          AI-generated reasoning
        </span>
      </div>
    )
  }
  if (source === 'edited') {
    return (
      <div className="flex items-center gap-1.5">
        <User className="h-3 w-3" style={{ color: '#a17a1f' }} />
        <span
          className="text-[10px] font-semibold uppercase tracking-wider"
          style={{ color: '#a17a1f' }}
        >
          Edited from AI
        </span>
      </div>
    )
  }
  return (
    <div className="flex items-center gap-1.5">
      <User className="h-3 w-3" style={{ color: 'var(--color-text-muted)' }} />
      <span
        className="text-[10px] font-semibold uppercase tracking-wider"
        style={{ color: 'var(--color-text-muted)' }}
      >
        Manual entry
      </span>
    </div>
  )
}

function Metadata({
  modelUsed,
  generatedAt,
  editedAt,
  source,
}: {
  modelUsed?: string
  generatedAt?: string
  editedAt?: string
  source: FieldSource
}) {
  const bits: string[] = []
  if (modelUsed) bits.push(prettyModel(modelUsed))
  if (generatedAt) bits.push(`generated ${formatRelative(generatedAt)}`)
  if (editedAt && source === 'edited') bits.push(`edited ${formatRelative(editedAt)}`)
  if (bits.length === 0) return null
  return (
    <p
      className="mt-2 border-t pt-2 text-[10px]"
      style={{
        color: 'var(--color-text-muted)',
        borderColor: 'var(--color-border-subtle)',
      }}
    >
      {bits.join(' · ')}
    </p>
  )
}

function prettyModel(model: string): string {
  // claude-sonnet-4-20250514 → Claude Sonnet 4
  if (model.startsWith('claude-')) {
    const parts = model.replace(/^claude-/, '').split('-')
    const name = parts[0]
    const major = parts[1]
    return `Claude ${cap(name)} ${major ?? ''}`.trim()
  }
  if (model.startsWith('gemini-')) return `Gemini ${model.replace(/^gemini-/, '')}`
  return model
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return iso
  const diffMs = Date.now() - then
  const mins = Math.floor(diffMs / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d ago`
  return new Date(iso).toLocaleDateString()
}
